// React
import { ReactElement } from 'react';
// Context
import { useMags } from '@utmb/contexts/MagsContext';
// Interfaces
import { IconsEnum } from '@utmb/types/icons';
// Utils
import { useTranslation } from 'next-i18next';
import debounce from 'lodash.debounce';
// Features
import styles from '@utmb/features/utmb-news/magazine.module.scss';
// Design System
import { DropDown } from '@utmb/design-system/dropdown/Dropdown';
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { ToolBar } from '@utmb/design-system/toolbar/Toolbar';
import ReactSelectFilter from '@utmb/features/react-select-filter/ReactSelectFilter';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';

export const MagazineFilters = (): ReactElement => {
    const { t } = useTranslation();

    const {
        search,
        selectedThemes,
        selectedType,
        filters: { themes, types },
        handleCheckType,
        handleCheckTheme,
        handleChangeSearch,
    } = useMags();

    const handleInputChange = async ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): Promise<void> => handleChangeSearch(value);

    return (
        <ToolBar className={styles.toolbar}>
            <DropDown
                button={({ isExpanded }) => (
                    <>
                        <p className={styles.filter_label}>
                            <Font.OxaniumRegular mobile="14">{t('news.type')}</Font.OxaniumRegular>
                            <div className={styles.filter_content}>
                                <Font.OxaniumRegular mobile="14" className={styles.filter_label_value}>
                                    {selectedType ? types.find((t) => t.value === selectedType)!.label : '-'}
                                </Font.OxaniumRegular>
                                {selectedType && (
                                    <button
                                        className={styles.reset_button}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCheckType(selectedType);
                                        }}
                                    >
                                        <Icon variant={IconsEnum.TRASH_SECOND} color={getColorStyle(ColorTypesEnum.RED)} />
                                    </button>
                                )}
                            </div>
                        </p>
                        <Icon variant={IconsEnum[isExpanded ? 'CHEVRON_UP' : 'CHEVRON_DOWN']} />
                    </>
                )}
                className={`${styles.drop} ${styles.drop_type}`}
            >
                <ReactSelectFilter
                    options={types}
                    selectedOptions={selectedType ? [selectedType] : []}
                    handleCheck={handleCheckType}
                    filterType={'default'}
                    placeholder={t('reactSelectFilter.filterMediaType')}
                />
            </DropDown>
            <DropDown
                button={({ isExpanded }) => (
                    <>
                        <p className={styles.filter_label}>
                            <Font.OxaniumRegular mobile="14">{t('news.theme')}</Font.OxaniumRegular>
                            <div className={styles.filter_content}>
                                <Font.OxaniumRegular mobile="14" className={styles.filter_label_value}>
                                    {selectedThemes.length > 0 ? selectedThemes.map((theme) => themes.find((t) => t.value === theme)!.label).join(', ') : '-'}
                                </Font.OxaniumRegular>
                                {selectedThemes.length > 0 && (
                                    <button
                                        className={styles.reset_button}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            selectedThemes.map((t) => handleCheckTheme(t));
                                        }}
                                    >
                                        <Icon variant={IconsEnum.TRASH_SECOND} color={getColorStyle(ColorTypesEnum.RED)} />
                                    </button>
                                )}
                            </div>
                        </p>
                        <Icon variant={IconsEnum[isExpanded ? 'CHEVRON_UP' : 'CHEVRON_DOWN']} />
                    </>
                )}
                className={`${styles.drop} ${styles.drop_type}`}
            >
                <ReactSelectFilter
                    options={themes}
                    selectedOptions={selectedThemes}
                    handleCheck={handleCheckTheme}
                    filterType={'default'}
                    placeholder={t('reactSelectFilter.filterMediaTheme')}
                />
            </DropDown>
            <div className={styles.input_text}>
                <input onChange={debounce(handleInputChange, 300)} type="text" placeholder={t('news.search')} defaultValue={search} />
            </div>
        </ToolBar>
    );
};
